import React from 'react';
import {
  MapPin,
  Rocket,
  UsersRound,
  Network,
  BadgeCheck,
  Wallet,
} from 'lucide-react';

const benefits = [
  {
    id: 'local-presence',
    icon: MapPin,
    title: 'Local Presence in China',
    description:
      'Tower Technology is based in Xiamen, China, allowing us to support industrial projects locally and mobilize personnel according to project requirements.',
  },
  {
    id: 'faster-mobilization',
    icon: Rocket,
    title: 'Faster Mobilization',
    description:
      'Local personnel can be deployed without the long international travel arrangements required when sending every specialist from overseas.',
  },
  {
    id: 'flexible-personnel',
    icon: UsersRound,
    title: 'Flexible Personnel Support',
    description:
      'Support can be arranged for specific project phases, short-term assignments or longer project periods — from individual specialists to project teams.',
  },
  {
    id: 'local-coordination',
    icon: Network,
    title: 'Local Coordination',
    description:
      'We help bridge the distance between overseas project teams and activities in China through local communication, technical follow-up and coordination with contractors, suppliers and site teams.',
  },
  {
    id: 'qualified-personnel',
    icon: BadgeCheck,
    title: 'Qualified Technical Personnel',
    description:
      'Access engineers, supervisors and technical specialists selected according to the technical requirements and responsibilities of the project.',
  },
  {
    id: 'reduced-overhead',
    icon: Wallet,
    title: 'Reduced Project Overhead',
    description:
      'Using local technical personnel can reduce international travel, accommodation, rotation and mobilization requirements while maintaining professional on-site project support.',
  },
];

export const WhyChooseUs = () => {
  return (
    <section
      id="why-choose-us"
      data-testid="why-choose-us-section"
      className="py-24 bg-white relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gray-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
              Local technical support in China — reducing distance, response time and project complexity.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.id}
                  data-testid={`why-us-card-${benefit.id}`}
                  className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-7 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 hover:border-blue-200"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl shadow-md mb-5">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
